/**
 * DepartmentPicker — FK picker for recruiting departments with inline create.
 *
 * Recruiting jobs reference recruiting_departments (recruiting-owned), so this
 * pulls from the recruiting departments endpoint — not user-management groups.
 * Wraps the shared EntityPickerBase and pairs it with DepartmentQuickCreateDialog
 * so a user can create a department without leaving the Create Job dialog.
 */
import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Building } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { EntityPickerBase } from '../../../shared/components/common';
import { useDepartments } from '../hooks/useDepartments';
import { DepartmentQuickCreateDialog } from './DepartmentQuickCreateDialog';
import type { Department } from '../services/recruitingApi';

interface DepartmentPickerProps {
  /** Currently selected department ID (empty string when none) */
  value: string;
  /** Callback when a department is selected or cleared */
  onChange: (departmentId: string) => void;
  /** Optional label above the picker */
  label?: string;
  /** Placeholder shown in the trigger when nothing is selected */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Show the "None" option to clear selection */
  allowClear?: boolean;
  /** Whether the field is required (shows * on label) */
  required?: boolean;
  /** Hide the "Add new" action (useful in filter contexts) */
  hideAddNew?: boolean;
}

export function DepartmentPicker({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  className,
  allowClear = false,
  required = false,
  hideAddNew = false,
}: DepartmentPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createDialogName, setCreateDialogName] = useState('');

  const { data: departmentsData } = useDepartments();

  const departments = useMemo(() => {
    const all = departmentsData ?? [];
    const query = search.trim().toLowerCase();
    if (!query) return all;
    return all.filter((d) => d.name.toLowerCase().includes(query));
  }, [departmentsData, search]);

  const handleSelect = useCallback((departmentId: string) => {
    onChange(departmentId);
    setOpen(false);
    setSearch('');
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange('');
    setOpen(false);
    setSearch('');
  }, [onChange]);

  const handleAddNew = useCallback(() => {
    setCreateDialogName(search.trim());
    setOpen(false);
    setShowCreateDialog(true);
  }, [search]);

  const handleCreated = useCallback((department: Department) => {
    onChange(department.id);
    setSearch('');
    setShowCreateDialog(false);
  }, [onChange]);

  const addNewLabel = search.trim()
    ? t('pickers.createDepartmentWithName', { name: search.trim() })
    : t('pickers.addNewDepartment');

  return (
    <>
      <EntityPickerBase<Department>
        value={value}
        items={departments}
        getItemLabel={(department) => department.name}
        renderItem={(department, isSelected) => (
          <>
            <Check className={cn('me-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
            <Building className="me-2 h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate">{department.name}</span>
          </>
        )}
        label={label}
        placeholder={placeholder ?? t('pickers.selectDepartment')}
        searchPlaceholder={t('pickers.searchDepartments')}
        emptyMessage={t('pickers.noDepartmentsFound')}
        disabled={disabled}
        className={className}
        required={required}
        search={search}
        onSearchChange={setSearch}
        open={open}
        onOpenChange={setOpen}
        onSelect={handleSelect}
        allowClear={allowClear}
        onClear={handleClear}
        hideAddNew={hideAddNew}
        addNewLabel={addNewLabel}
        onAddNew={handleAddNew}
      />

      <DepartmentQuickCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={handleCreated}
        defaultName={createDialogName}
      />
    </>
  );
}
